/**
 * Audit Center · Catálogo de auditorías históricas y futuras.
 * Filtros por categoría, prioridad, estado, búsqueda.
 * Viewer integrado de markdown.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import {
  FileText, MagnifyingGlass, Funnel, ArrowsClockwise, CheckCircle, Clock,
  WarningCircle, Archive, X as XIcon, ArrowSquareOut,
} from "@phosphor-icons/react";

const CATEGORIES = [
  ["", "Todas"],
  ["branding", "Branding"], ["uxui", "UI/UX"], ["marketplace", "Marketplace"],
  ["seo", "SEO"], ["taxonomy", "Taxonomía"], ["monetization", "Monetización"],
  ["leads", "Leads"], ["reservations", "Reservas"], ["infra", "Infraestructura"],
  ["security", "Seguridad"], ["growth", "Growth"], ["social", "Redes Sociales"],
  ["competition", "Competencia"], ["operations", "Operaciones"], ["ai", "IA"],
  ["founderos", "Founder OS"],
];

const PRIORITIES = [["", "Todas"], ["P0", "P0 Crítica"], ["P1", "P1 Importante"], ["P2", "P2 Menor"], ["P3", "P3 Opcional"]];
const STATUSES = [["", "Todos"], ["open", "Abierta"], ["in_progress", "En progreso"], ["resolved", "Resuelta"], ["archived", "Archivada"]];

const STATUS_META = {
  open:        { label: "Abierta",     icon: WarningCircle, cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  in_progress: { label: "En progreso", icon: Clock,         cls: "text-[var(--ag-gold)] border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10" },
  resolved:    { label: "Resuelta",    icon: CheckCircle,   cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  archived:    { label: "Archivada",   icon: Archive,       cls: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10" },
};

const PRIORITY_CLS = {
  P0: "text-red-300 border-red-500/40 bg-red-500/10",
  P1: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  P2: "text-[var(--ag-gold)] border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10",
  P3: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10",
};

export default function AuditCenter() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ by_priority: {}, by_status: {}, by_category: {} });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: "", priority: "", status: "", q: "" });
  const [viewing, setViewing] = useState(null);
  const [viewContent, setViewContent] = useState({ content: "", loading: false, error: null });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const { data } = await api.get(`/founder-os/audits?${params.toString()}`);
      setItems(data.items || []);
      setStats(data.stats || { by_priority: {}, by_status: {}, by_category: {} });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const openViewer = async (audit) => {
    setViewing(audit);
    setViewContent({ content: "", loading: true, error: null });
    try {
      const { data } = await api.get(`/founder-os/audits/${audit.id}/content`);
      setViewContent({ content: data.content, loading: false, error: null });
    } catch (e) {
      setViewContent({ content: "", loading: false, error: e?.response?.data?.detail || "Error" });
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="founder-audits">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <FileText size={11} weight="fill" /> Auditorías
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
            Catálogo de auditorías
          </h1>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            {items.length} auditorías · todas las decisiones documentadas en un solo lugar
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-3 py-2 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50 transition-colors"
          data-testid="audits-refresh"
        >
          <ArrowsClockwise size={13} /> Refrescar
        </button>
      </header>

      {/* Stats chips */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        {Object.entries(stats.by_priority || {}).sort().map(([p, n]) => (
          <div key={p} className={`px-3 py-2 rounded border ${PRIORITY_CLS[p] || "border-[var(--ag-border)]"}`}>
            <div className="text-[9px] uppercase tracking-wider opacity-70">Prioridad</div>
            <div className="font-bold tabular-nums">{p}: {n}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center" data-testid="audits-filters">
        <Funnel size={14} className="text-[var(--ag-text-dim)]" />
        <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                className="bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded px-2 py-1.5 text-xs"
                data-testid="audits-filter-category">
          {CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                className="bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded px-2 py-1.5 text-xs"
                data-testid="audits-filter-priority">
          {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded px-2 py-1.5 text-xs"
                data-testid="audits-filter-status">
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--ag-text-muted)]" />
          <input
            type="text"
            placeholder="Buscar título, tag…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="w-full bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded pl-7 pr-2 py-1.5 text-xs"
            data-testid="audits-search"
          />
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="text-xs text-[var(--ag-text-dim)]">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-[var(--ag-text-dim)] py-8 text-center">Sin auditorías para los filtros aplicados.</div>
      ) : (
        <div className="space-y-2" data-testid="audits-list">
          {items.map((a) => {
            const stm = STATUS_META[a.status] || STATUS_META.open;
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => openViewer(a)}
                className="w-full text-left p-4 rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] hover:border-[var(--ag-gold)]/50 transition-colors group"
                data-testid={`audit-${a.slug}`}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${PRIORITY_CLS[a.priority] || ""}`}>
                        {a.priority}
                      </span>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${stm.cls}`}>
                        <stm.icon size={9} weight="fill" /> {stm.label}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--ag-border)] text-[var(--ag-text-muted)]">
                        {a.category}
                      </span>
                      {a.score != null && (
                        <span className="text-[9px] tabular-nums font-bold text-[var(--ag-gold)]">
                          Score {a.score}/100
                        </span>
                      )}
                    </div>
                    <div className="mt-2 font-display text-base font-bold tracking-tight group-hover:text-[var(--ag-gold)] transition-colors">
                      {a.title}
                    </div>
                    <p className="text-xs text-[var(--ag-text-dim)] mt-1 line-clamp-2">{a.summary}</p>
                    {a.tags && a.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {a.tags.map(t => (
                          <span key={t} className="text-[9px] text-[var(--ag-text-muted)] bg-[var(--ag-midnight)]/40 px-1.5 py-0.5 rounded">#{t}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <ArrowSquareOut size={14} className="text-[var(--ag-text-muted)] group-hover:text-[var(--ag-gold)] transition-colors shrink-0" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Viewer modal */}
      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setViewing(null)}
             data-testid="audit-viewer-backdrop">
          <div className="bg-[var(--ag-surface)] border border-[var(--ag-border)] rounded-lg max-w-4xl w-full max-h-[85vh] flex flex-col"
               onClick={(e) => e.stopPropagation()}
               data-testid="audit-viewer">
            <div className="p-4 border-b border-[var(--ag-border)] flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[9px] uppercase tracking-wider text-[var(--ag-text-muted)]">{viewing.category} · {viewing.priority}</div>
                <h2 className="font-display text-xl font-bold tracking-tight mt-1 truncate">{viewing.title}</h2>
              </div>
              <button type="button" onClick={() => setViewing(null)}
                      className="text-[var(--ag-text-dim)] hover:text-white p-1.5 rounded shrink-0"
                      data-testid="audit-viewer-close">
                <XIcon size={18} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 font-mono text-xs leading-relaxed whitespace-pre-wrap text-[var(--ag-text-dim)]"
                 data-testid="audit-viewer-content">
              {viewContent.loading ? "Cargando…"
                : viewContent.error ? <span className="text-red-400">{viewContent.error}</span>
                : viewContent.content}
            </div>
            {viewing.file_path && (
              <div className="p-3 border-t border-[var(--ag-border)] text-[10px] text-[var(--ag-text-muted)] font-mono">
                {viewing.file_path}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
